// Identifiers only: names, contact details and clinical text never belong in URLs.
export const clientWorkspaceHref = (patientId, orgId) => {
  const query = new URLSearchParams({ client: patientId, org: orgId });
  return `/consultationSummary?${query}`;
};

export const isWorkspaceId = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

// Navigation state is a hint, never authority. Select an org only when the
// cached patient identifiers specify exactly one possible organization. A
// persisted selected org cannot establish the origin of an ambiguous old link.
export function legacyClientOrgId(patient) {
  const candidates = [...new Set([
    ...(Array.isArray(patient?.team_ids) ? patient.team_ids : []), patient?.teamid,
  ].filter(isWorkspaceId))];
  return candidates.length === 1 ? candidates[0] : null;
}

const encryptedPatientFields = [
  'name', 'phone_code', 'phoneNumber', 'firstName', 'lastName', 'gender', 'age',
  'birth_date', 'hkid', 'remarks', 'status', 'latestComplaint', 'insights',
  'emergency_contact', 'emergency_contact_person', 'presenting_concerns', 'email',
];

const unavailable = () => new Error('CLIENT_UNAVAILABLE');
const check = result => {
  if (result.error) throw result.error;
  return result.data;
};
const decrypt = async (db, value, signal) => {
  if (value == null || value === '') return value;
  return check(await db.rpc('decrypt_text', { encrypted_data: value }).abortSignal(signal));
};

// Use the same authenticated tables, RLS and decrypt RPC as the Client List.
// Never trust navigation state, Redux's cached role, or a URL as authorization.
export async function loadClientWorkspace(db, { patientId, orgId, userId, signal }) {
  if (!isWorkspaceId(patientId) || !isWorkspaceId(orgId)) throw unavailable();
  const sessionResult = await db.auth.getSession();
  if (sessionResult.error || sessionResult.data?.session?.user?.id !== userId) throw unavailable();

  // maybeSingle deliberately rejects duplicate memberships; never select an
  // arbitrary role with limit(1). This is a consistency check, not a substitute
  // for secure database membership/clinical RLS (see the release gate in docs).
  const membership = check(await db.from('team_users').select('user_id, role')
    .eq('team_id', orgId).eq('user_id', userId).abortSignal(signal).maybeSingle());
  if (!membership || Array.isArray(membership)) throw unavailable();

  const patient = check(await db.from('patients').select('*').eq('id', patientId)
    .or(`team_ids.cs.{${orgId}},teamid.eq.${orgId}`).abortSignal(signal).maybeSingle());
  if (!patient) throw unavailable();

  const [teamResult, membersResult] = await Promise.all([
    db.from('teams').select('id, name').eq('id', orgId).abortSignal(signal).maybeSingle(),
    db.from('team_users').select('user_id, role').eq('team_id', orgId).abortSignal(signal),
  ]);
  const team = check(teamResult);
  const members = check(membersResult) || [];
  if (!team) throw unavailable();
  const users = members.length ? check(await db.from('user_accounts').select('id, name')
    .in('id', members.map(member => member.user_id)).abortSignal(signal)) || [] : [];
  const roles = Object.fromEntries(members.map(member => [member.user_id, member.role]));
  const [patientValues, orgName, teamMembersData] = await Promise.all([
    Promise.all(encryptedPatientFields.map(field => decrypt(db, patient[field], signal))),
    decrypt(db, team.name, signal),
    Promise.all(users.map(async user => ({ id: user.id, name: await decrypt(db, user.name, signal), role: roles[user.id] ?? null }))),
  ]);
  const decryptedPatient = { ...patient, ...Object.fromEntries(encryptedPatientFields.map((field, i) => [field, patientValues[i]])) };
  return {
    currentOrg: { id: team.id, name: orgName }, role: membership.role,
    params: { patient: decryptedPatient, teamMembersData, latestComplaint: decryptedPatient.latestComplaint },
  };
}
